import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useReducedMotion, useScroll } from 'framer-motion';
import { useTheme } from '../../theme';

const PaletteRailScene = lazy(() => import('./PaletteRailScene'));

/*
  How the palette field is kept from fighting the copy on top of it.
  Change TREATMENT to switch approach; each is a different trade between how
  much of the field stays visible and how much contrast the text gets.

    'oval'   original — an elliptical hole clears the centre. Best contrast,
             but the cleared area reads as a blob.
    'sides'  clears a straight vertical column instead of an ellipse. Same
             protection for centred copy, no blob: it reads as a margin.
    'band'   clears a horizontal band through the middle of the viewport.
    'faint'  no mask at all. The whole field stays, dropped in opacity and
             blurred harder so it becomes an ambient colour wash.
    'full'   no mask, no dimming. Everything visible, copy fends for itself.
    'off'    no backdrop at all.
*/
type Treatment = 'oval' | 'sides' | 'band' | 'faint' | 'full' | 'off';
// Cast widens past literal narrowing, so switching the value keeps compiling.
const TREATMENT = 'band' as Treatment;

const MASKS: Partial<Record<Treatment, string>> = {
  oval: 'radial-gradient(ellipse 68% 58% at 50% 45%, transparent 0%, transparent 52%, rgba(0,0,0,0.45) 78%, #000 100%)',
  sides:
    'linear-gradient(to right, #000 0%, #000 14%, rgba(0,0,0,0.12) 32%, rgba(0,0,0,0.12) 68%, #000 86%, #000 100%)',
  /*
    Stops clear well above the heading. Copy runs roughly 18%-66% of the
    viewport, so the field has to be out of the way by 14% — which leaves the
    top strip thin, and puts most of the visible field below the content.
  */
  band: 'linear-gradient(to bottom, #000 0%, #000 4%, rgba(0,0,0,0.10) 14%, rgba(0,0,0,0.10) 70%, #000 88%, #000 100%)',
};

const SceneBackdrop: React.FC = () => {
  const reduce = useReducedMotion();
  const { isDark } = useTheme();
  const { scrollYProgress } = useScroll();
  const [ready, setReady] = useState(false);
  const [count, setCount] = useState(14);
  const [compact, setCompact] = useState(false);

  // Mount only once the page has painted, so the backdrop never delays LCP.
  useEffect(() => {
    if (reduce) return;

    /*
      Width-only, deliberately. A phone fires `resize` on every URL-bar collapse
      during a scroll; reacting to those would re-render the WebGL scene
      mid-gesture and stutter the scroll. Only a real width change — a rotation,
      or a desktop window drag — can change how the field is sized.
    */
    let lastWidth = -1;
    const sizeField = () => {
      const width = window.innerWidth;
      if (width === lastWidth) return;
      lastWidth = width;

      const narrow = width < 768;
      setCount(narrow ? 7 : 14);
      setCompact(narrow);
    };
    sizeField();
    window.addEventListener('resize', sizeField);

    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => setReady(true), { timeout: 2000 })
      : window.setTimeout(() => setReady(true), 600);

    return () => {
      window.removeEventListener('resize', sizeField);
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle as number);
      else window.clearTimeout(idle as number);
    };
  }, [reduce]);

  // Motion is the whole point of this layer, so it is simply absent when the
  // visitor has asked for reduced motion.
  if (reduce || !ready || TREATMENT === 'off') return null;

  // 'faint' trades the mask for a weaker, softer field across the whole frame.
  const faint = TREATMENT === 'faint';
  const baseOpacity = compact ? (isDark ? 0.32 : 0.36) : isDark ? 0.7 : 0.78;
  const mask = MASKS[TREATMENT];

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 select-none"
      style={{
        opacity: faint ? baseOpacity * 0.5 : baseOpacity,
        /*
          No filter on a phone: a CSS filter over a full-screen fixed canvas is
          re-rasterized on every scrolled frame, and the 1px softening it buys
          is invisible at this size anyway.
        */
        filter: faint ? 'blur(7px) saturate(115%)' : compact ? undefined : 'blur(1px)',
        maskImage: mask,
        WebkitMaskImage: mask,
      }}
    >
      <Suspense fallback={null}>
        <PaletteRailScene
          progress={scrollYProgress}
          count={count}
          compact={compact}
        />
      </Suspense>
    </div>
  );
};

export default SceneBackdrop;
